import type { AnalysisNode, Process, Recording, RecordingDetails } from "../openapi-aliases";

const DAY = 86_400_000;
const timestamp = (value: string) =>
  Date.parse(
    value.length === 10 ? `${value}T00:00:00Z` : value.endsWith("Z") ? value : `${value}Z`,
  );

// One anchor per loaded demo keeps dates stable through navigation and makes the
// shipped snapshots useful in rolling date filters without regenerating them.
export const demoToday = new Date().toISOString().slice(0, 10);

function shiftDates<T>(value: T, delta: number): T {
  const visit = (item: unknown, key = ""): unknown => {
    if (typeof item === "string") {
      if (/^\d{4}-\d{2}-\d{2}(T[\d:.]+Z?)?$/.test(item)) {
        const shifted = new Date(timestamp(item) + delta).toISOString();
        return item.length === 10 ? shifted.slice(0, 10) : shifted;
      }
      if (/^\d{4}-\d{2}-\d{2}_\d{2}_\d{2}_\d{2}_\d{3}\./.test(item)) {
        const iso = `${item.slice(0, 10)}T${item.slice(11, 19).replaceAll("_", ":")}.${item.slice(20, 23)}Z`;
        return (
          new Date(Date.parse(iso) + delta)
            .toISOString()
            .slice(0, 23)
            .replace("T", "_")
            .replaceAll(":", "_")
            .replace(".", "_") + item.slice(23)
        );
      }
    }
    if (typeof item === "number" && (key === "startTimestamp" || key === "endTimestamp"))
      return item + delta / 1000;
    if (Array.isArray(item)) return item.map(entry => visit(entry));
    if (item && typeof item === "object")
      return Object.fromEntries(
        Object.entries(item).map(([name, entry]) => [name, visit(entry, name)]),
      );
    return item;
  };
  return visit(value) as T;
}

/** Split only between completed cases: interleaved cases stay in one recording. */
export function buildDemoDataset(
  rawProcesses: Process[],
  rawRecordings: RecordingDetails[],
  splitRecordingIds: Set<string>,
  today = demoToday,
) {
  const processes: Process[] = JSON.parse(JSON.stringify(rawProcesses));
  const instanceIds = new Map<string, string>();
  const details: Record<string, RecordingDetails> = {};
  for (const [recordingIndex, original] of rawRecordings.entries()) {
    const instances = processes
      .flatMap(process => process.instances)
      .filter(instance => instance.source.recordingId === original.id)
      .sort((a, b) => a.source.traceIndex - b.source.traceIndex);
    const start = timestamp(original.date_started);
    const end = timestamp(original.date_ended!);
    const data = original.status.status === "completed" ? original.status.result : null;
    const nodes = new Map(
      [...(data?.nodes ?? []), ...(data?.orphans ?? [])].map(node => [node.id, node]),
    );
    const children = (roots: AnalysisNode[]) => {
      const ids = new Set<number>();
      const add = (node: AnalysisNode) => {
        if (ids.has(node.id)) return;
        ids.add(node.id);
        for (const id of node.children ?? []) {
          const child = nodes.get(id);
          if (child) add(child);
        }
      };
      roots.forEach(add);
      return ids;
    };
    const coverageExtent = (node: AnalysisNode): [number, number] => {
      const covered = [...children([node])].flatMap(id => nodes.get(id)?.coverage ?? []);
      return covered.length
        ? [
            Math.min(...covered.map(interval => timestamp(interval[0]))),
            Math.max(...covered.map(interval => timestamp(interval[1]))),
          ]
        : [start, start];
    };
    const sorted = [
      ...instances.map(instance => [
        timestamp(instance.source.traceStart),
        timestamp(instance.source.traceEnd),
      ]),
      ...(data?.orphans ?? []).map(coverageExtent),
    ].sort((a, b) => a[0] - b[0]);
    const gaps: number[] = [];
    let previousEnd = start;
    for (const [nextStart, nextEnd] of sorted) {
      if (previousEnd > start && nextStart > previousEnd) gaps.push((previousEnd + nextStart) / 2);
      previousEnd = Math.max(previousEnd, nextEnd);
    }
    let boundaries = [start, end];
    if (splitRecordingIds.has(original.id) && gaps.length >= 2) {
      let best = Number.POSITIVE_INFINITY;
      for (let a = 0; a < gaps.length - 1; a++) {
        for (let b = a + 1; b < gaps.length; b++) {
          const durations = [gaps[a] - start, gaps[b] - gaps[a], end - gaps[b]];
          const score = durations.reduce((sum, duration) => sum + duration ** 2, 0);
          if (score < best) {
            best = score;
            boundaries = [start, gaps[a], gaps[b], end];
          }
        }
      }
    }
    const parts = boundaries.slice(0, -1).map((from, part) =>
      instances.filter(instance => {
        const time = timestamp(instance.source.traceStart);
        return time >= from && time < boundaries[part + 1];
      }),
    );
    for (let part = 0; part < boundaries.length - 1; part++) {
      const from = boundaries[part];
      const to = boundaries[part + 1];
      const selected = parts[part];
      const id =
        part === 0
          ? original.id
          : `${original.id.slice(0, -4)}${part.toString(16).padStart(4, "0")}`;
      const daysAgo = boundaries.length === 4 ? [40, 18, 3][part] : 5 + (recordingIndex % 5);
      const newStart = timestamp(today) - (daysAgo + (recordingIndex % 3)) * DAY + 8 * 3_600_000;
      const delta = newStart - from;
      for (const [traceIndex, instance] of selected.entries()) {
        const oldId = instance.id;
        Object.assign(instance, shiftDates(instance, delta));
        instance.id = `${id}:${traceIndex}`;
        instance.source.recordingId = id;
        instance.source.recordingDate = new Date(newStart).toISOString().slice(0, 10);
        instance.source.traceIndex = traceIndex;
        instanceIds.set(oldId, instance.id);
      }
      const orphans = (data?.orphans ?? []).filter(node => {
        const time = coverageExtent(node)[0];
        return time >= from && (time < to || part === boundaries.length - 2);
      });
      const roots = selected.map(instance => nodes.get(instance.analysisNodeId)!).filter(Boolean);
      const included =
        boundaries.length > 2
          ? children([...roots, ...orphans])
          : new Set(data?.nodes.map(node => node.id));
      const recording = shiftDates(original, delta);
      recording.id = id;
      recording.date_started = new Date(newStart).toISOString();
      recording.date_ended = new Date(to + delta).toISOString();
      recording.relative_path_on_disk = `demo_${id}`;
      if (data)
        recording.status = {
          status: "completed",
          result: {
            ...data,
            startTimestamp: newStart / 1000,
            endTimestamp: (to + delta) / 1000,
            durationSeconds: (to - from) / 1000,
            nodes: shiftDates(
              data.nodes.filter(node => included.has(node.id)),
              delta,
            ),
            orphans: shiftDates(orphans, delta),
          },
        };
      details[id] = recording;
    }
  }
  for (const process of processes) {
    process.sources = process.instances.map(instance => instance.source);
    process.firstSeen = process.sources.map(source => source.traceStart).sort()[0];
    process.lastSeen = process.sources
      .map(source => source.traceEnd)
      .sort()
      .at(-1)!;
  }
  const recordings: Recording[] = Object.values(details)
    .map(recording => ({
      ...recording,
      status: { status: "completed" as const, result: null },
    }))
    .sort((a, b) => b.date_started.localeCompare(a.date_started));
  return { processes, recordings, details, instanceIds };
}
